import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import UserDetailModal from '../../shared/components/UserDetailModal';
import { useUserNames, formatUserLabel } from '../../shared/hooks/useUserNames';
import {
  assignDispute,
  getDispute,
  getDisputeImageUrl,
  getDisputeMessages,
  getTradeChat,
  getTradeChatImageUrl,
  requestDisputeEvidence,
  resolveDispute,
  sendDisputeMessage,
  unassignDispute,
} from './api';
import {
  AdminSummary,
  DisputeAttachment,
  DisputeEvent,
  DisputeEvidence,
  DisputeMessage,
  DisputePriority,
  DisputeRecord,
  DisputeResolution,
  DisputeStatus,
  TradeChatMessage,
} from '../../shared/types';
import { getAdmins } from '../../shared/api/admins';
import { useAuth } from '../auth/AuthContext';

const REASON_LABELS: Record<string, string> = {
  payment_not_received: 'Payment not received',
  partial_payment: 'Partial payment',
  wrong_amount: 'Wrong amount paid',
  fake_proof: 'Fake payment proof',
  third_party_payment: 'Third-party payment',
  blocked_funds: 'Blocked/illegal funds',
  assets_not_released: 'Assets not released',
  counterparty_unresponsive: 'Counterparty unresponsive',
  other: 'Other',
};

const priorityClass = (p: DisputePriority) => {
  switch (p) {
    case 'critical': return 'bg-red-500/15 text-red-400 border border-red-500/30';
    case 'high': return 'bg-orange-500/15 text-orange-300 border border-orange-500/30';
    case 'medium': return 'bg-amber-500/15 text-amber-300 border border-amber-500/30';
    default: return 'bg-slate-500/15 text-slate-300 border border-slate-500/30';
  }
};

const statusClass = (s: DisputeStatus): string => {
  switch (s) {
    case 'resolved': return 'badge-success';
    case 'cancelled': return 'badge-danger';
    case 'under_review': return 'badge-info';
    default: return 'badge-warning';
  }
};

const errMsg = (err: unknown, fallback: string): string => {
  if (err && typeof err === 'object' && 'response' in err) {
    const e = err as { response?: { data?: { error?: string; message?: string } } };
    return e.response?.data?.error ?? e.response?.data?.message ?? fallback;
  }
  return fallback;
};

const isImageUrl = (url: string): boolean =>
  /\.(jpe?g|png|gif|webp|bmp|svg|avif)(\?.*)?$/i.test(url) || url.includes('/storage/');

// Loads an authenticated blob URL and renders it as an inline image.
const BlobImage: React.FC<{ load: () => Promise<string>; description?: string | null }> = ({ load, description }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let created: string | null = null;
    load()
      .then((url) => {
        if (!active) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setSrc(url);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) return <p className="text-xs text-red-400 italic">Image unavailable</p>;
  if (!src) return <p className="text-xs text-slate-500 italic">Loading image…</p>;
  return (
    <a href={src} target="_blank" rel="noreferrer noopener">
      <img
        src={src}
        alt={description || 'attachment'}
        className="max-h-48 rounded-lg border border-slate-700 object-contain"
      />
    </a>
  );
};

const Attachment: React.FC<{ item: DisputeEvidence | DisputeAttachment; disputeId: number }> = ({ item, disputeId }) => {
  const description = item.description ?? undefined;
  if (item.path) {
    const path = item.path;
    return (
      <div>
        <BlobImage load={() => getDisputeImageUrl(disputeId, path)} description={description} />
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
    );
  }
  const url = item.url ?? '';
  return (
    <div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        className="text-indigo-400 hover:text-indigo-300 underline break-all"
      >
        {isImageUrl(url) ? (
          <img
            src={url}
            alt={description || 'attachment'}
            className="max-h-48 rounded-lg border border-slate-700 object-contain"
          />
        ) : (
          url
        )}
      </a>
      {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
    </div>
  );
};

const EvidenceList: React.FC<{ title: string; items: DisputeEvidence[] | null; accent: string; disputeId: number }> = ({ title, items, accent, disputeId }) => (
  <div className="card p-4 space-y-3">
    <h4 className={`text-sm font-semibold ${accent}`}>{title}</h4>
    {!items || items.length === 0 ? (
      <p className="text-xs text-slate-500">No attachments provided.</p>
    ) : (
      <ul className="space-y-2">
        {items.map((ev, idx) => (
          <li key={idx} className="text-sm">
            <Attachment item={ev} disputeId={disputeId} />
            <p className="text-[10px] text-slate-600">uploaded by #{ev.uploaded_by}</p>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// Trade (buyer/seller) chat shown to the admin reviewing a dispute.
const TradeChatPanel: React.FC<{ disputeId: number; buyerId?: number | null }> = ({ disputeId, buyerId }) => {
  const [messages, setMessages] = useState<TradeChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getTradeChat(disputeId)
      .then((m) => active && setMessages(m))
      .catch(() => active && setError('Failed to load trade chat'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [disputeId]);

  return (
    <div className="card p-4">
      <h3 className="text-sm font-semibold text-slate-300 mb-1">Buyer/Seller trade chat</h3>
      <p className="text-[11px] text-slate-500 mb-3">
        The original conversation between buyer and seller for this trade.
      </p>
      {loading ? (
        <p className="text-xs text-slate-500 italic">Loading…</p>
      ) : error ? (
        <p className="text-xs text-red-400">{error}</p>
      ) : messages.length === 0 ? (
        <p className="text-xs text-slate-500 italic">No messages in the trade chat.</p>
      ) : (
        <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto pr-1">
          {messages.map((m) => {
            if (m.type === 'system') {
              return (
                <div key={m.id} className="mx-auto max-w-md bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-400 italic text-center">
                  {m.message}
                </div>
              );
            }
            const isBuyer = buyerId != null && Number(m.sender_id) === Number(buyerId);
            const align = isBuyer ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20';
            return (
              <div key={m.id} className={`max-w-xl rounded-lg border p-3 ${align}`}>
                <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-wider text-slate-500">
                  <span>{isBuyer ? 'Buyer' : 'Seller'} #{m.sender_id}</span>
                  <span>·</span>
                  <span>{new Date(m.created_at).toLocaleString()}</span>
                </div>
                {m.message && <p className="text-sm text-slate-200 whitespace-pre-wrap">{m.message}</p>}
                {m.has_image && (
                  <div className="mt-2">
                    <BlobImage load={() => getTradeChatImageUrl(disputeId, m.id)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Chronological list of who was assigned to the dispute and by whom.
const AssignmentHistory: React.FC<{
  events: DisputeEvent[] | undefined;
  adminLabel: (id: number | null | undefined) => string;
}> = ({ events, adminLabel }) => {
  const items = (events ?? []).filter(
    (e) =>
      e.event_type === 'assigned' ||
      e.event_type === 'reassigned' ||
      e.event_type === 'unassigned'
  );
  if (items.length === 0) return null;
  return (
    <div className="card p-4 space-y-3">
      <h4 className="text-sm font-semibold text-slate-300">Assignment history</h4>
      <ol className="space-y-2">
        {items.map((e) => {
          const toId = (e.data?.admin_id ?? null) as number | null;
          const fromId = (e.data?.from_admin_id ?? null) as number | null;
          const byId = (e.data?.assigned_by ?? e.actor_id ?? null) as number | null;
          return (
            <li key={e.id} className="text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <span className="text-slate-600 w-32 flex-shrink-0">
                  {new Date(e.created_at).toLocaleString()}
                </span>
                <span className="flex-1 text-slate-300">
                  {e.event_type === 'unassigned' ? (
                    <>
                      unassigned from <span className="font-medium">{adminLabel(fromId)}</span>
                    </>
                  ) : fromId ? (
                    <>
                      <span className="text-slate-500">{adminLabel(fromId)}</span>
                      <span className="text-slate-600"> → </span>
                      <span className="font-medium">{adminLabel(toId)}</span>
                    </>
                  ) : (
                    <>
                      assigned to <span className="font-medium">{adminLabel(toId)}</span>
                    </>
                  )}
                  {e.event_type !== 'unassigned' && byId != null && byId !== toId && (
                    <span className="text-slate-500"> · by {adminLabel(byId)}</span>
                  )}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const Timeline: React.FC<{ events: DisputeEvent[] | undefined }> = ({ events }) => {
  if (!events || events.length === 0) return null;
  return (
    <div className="card p-4 space-y-3">
      <h4 className="text-sm font-semibold text-slate-300">Timeline</h4>
      <ol className="space-y-2">
        {events.map((e) => (
          <li key={e.id} className="text-xs text-slate-400 flex items-start gap-2">
            <span className="text-slate-600 w-32 flex-shrink-0">{new Date(e.created_at).toLocaleString()}</span>
            <span className="uppercase text-[10px] font-semibold text-indigo-400 w-20 flex-shrink-0">
              {e.actor_type}
            </span>
            <span className="flex-1">
              <span className="text-slate-300 font-medium">{e.event_type.replace(/_/g, ' ')}</span>
              {e.data && Object.keys(e.data).length > 0 && (
                <span className="text-slate-500"> — {JSON.stringify(e.data)}</span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
};

const MessageBubble: React.FC<{ message: DisputeMessage; disputeId: number }> = ({ message, disputeId }) => {
  const isAdmin = message.sender_type === 'admin';
  const isSystem = message.sender_type === 'system';
  const align = isAdmin ? 'ml-auto bg-indigo-500/10 border-indigo-500/30'
    : isSystem ? 'mx-auto bg-slate-800/60 border-slate-700 text-center italic text-slate-400'
    : message.sender_type === 'buyer' ? 'bg-emerald-500/5 border-emerald-500/20'
    : 'bg-amber-500/5 border-amber-500/20';
  return (
    <div className={`max-w-xl rounded-lg border p-3 ${align} ${message.is_internal ? 'ring-1 ring-red-500/50' : ''}`}>
      <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-wider text-slate-500">
        <span>{message.sender_type}{message.sender_id ? ` #${message.sender_id}` : ''}</span>
        <span>·</span>
        <span>{new Date(message.created_at).toLocaleString()}</span>
        {message.is_internal && <span className="text-red-400 font-semibold">INTERNAL</span>}
      </div>
      {message.message && <p className="text-sm text-slate-200 whitespace-pre-wrap">{message.message}</p>}
      {message.attachments && message.attachments.length > 0 && (
        <ul className="mt-2 space-y-1.5 text-xs">
          {message.attachments.map((a, i) => (
            <li key={i}>
              <Attachment item={a} disputeId={disputeId} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const DisputeDetailPage: React.FC = () => {
  const { id } = useParams();
  const disputeId = Number(id);
  const navigate = useNavigate();
  const { admin } = useAuth();

  const [dispute, setDispute] = useState<DisputeRecord | null>(null);
  const [messages, setMessages] = useState<DisputeMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [messageText, setMessageText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolveOutcome, setResolveOutcome] = useState<DisputeResolution>('release_to_buyer');
  const [resolveReason, setResolveReason] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState('');

  const [admins, setAdmins] = useState<AdminSummary[]>([]);
  const [isUnassigning, setIsUnassigning] = useState(false);

  const [requestEvModalOpen, setRequestEvModalOpen] = useState(false);
  const [requestEvFrom, setRequestEvFrom] = useState<'buyer' | 'seller'>('buyer');
  const [requestEvNote, setRequestEvNote] = useState('');
  const [isRequestingEv, setIsRequestingEv] = useState(false);
  const [requestEvError, setRequestEvError] = useState('');

  const [partyUserId, setPartyUserId] = useState<number | null>(null);
  const [partyTitle, setPartyTitle] = useState<string>('');

  const openParty = (userId: number, label: string) => {
    setPartyUserId(userId);
    setPartyTitle(label);
  };

  const userNames = useUserNames([
    dispute?.opened_by_id,
    dispute?.against_user_id,
  ]);

  const hasPermission = (p: string) => {
    if (admin?.role === 'super_admin') return true;
    return admin?.permissions?.includes(p) ?? false;
  };
  const canResolve = hasPermission('p2p.resolve_dispute');
  const canChat = hasPermission('p2p.send_chat') || canResolve;

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [d, m] = await Promise.all([
        getDispute(disputeId),
        getDisputeMessages(disputeId),
      ]);
      setDispute(d);
      setMessages(m);
    } catch (err) {
      setError(errMsg(err, 'Failed to load dispute'));
      setDispute(null);
    } finally {
      setIsLoading(false);
    }
  }, [disputeId]);

  useEffect(() => {
    if (!Number.isFinite(disputeId)) return;
    load();
  }, [disputeId, load]);

  useEffect(() => {
    let active = true;
    getAdmins()
      .then((list) => active && setAdmins(list))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const adminLabel = (adminId: number | null | undefined): string => {
    if (adminId == null) return '—';
    const found = admins.find((a) => a.id === Number(adminId));
    return found ? found.name : `#${adminId}`;
  };

  const handleAssignToMe = async () => {
    try {
      await assignDispute(disputeId);
      await load();
    } catch (err) {
      setError(errMsg(err, 'Failed to assign'));
    }
  };

  const handleUnassign = async () => {
    setIsUnassigning(true);
    try {
      await unassignDispute(disputeId);
      await load();
    } catch (err) {
      setError(errMsg(err, 'Failed to unassign'));
    } finally {
      setIsUnassigning(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim()) return;
    setIsSendingMessage(true);
    try {
      await sendDisputeMessage(disputeId, { message: messageText, is_internal: isInternalNote });
      setMessageText('');
      setIsInternalNote(false);
      const m = await getDisputeMessages(disputeId);
      setMessages(m);
    } catch (err) {
      setError(errMsg(err, 'Failed to send message'));
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleResolve = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResolving(true);
    setResolveError('');
    try {
      await resolveDispute(disputeId, {
        outcome: resolveOutcome,
        reason: resolveReason || undefined,
      });
      setResolveModalOpen(false);
      setResolveReason('');
      await load();
    } catch (err) {
      setResolveError(errMsg(err, 'Failed to resolve'));
    } finally {
      setIsResolving(false);
    }
  };

  const handleRequestEvidence = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requestEvNote.trim()) return;
    setIsRequestingEv(true);
    setRequestEvError('');
    try {
      await requestDisputeEvidence(disputeId, { from: requestEvFrom, note: requestEvNote });
      setRequestEvModalOpen(false);
      setRequestEvNote('');
      await load();
    } catch (err) {
      setRequestEvError(errMsg(err, 'Failed to request evidence'));
    } finally {
      setIsRequestingEv(false);
    }
  };

  if (!Number.isFinite(disputeId)) {
    return (
      <Layout>
        <div className="p-6 text-red-400">Invalid dispute id.</div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error || !dispute) {
    return (
      <Layout>
        <div className="p-6 text-center space-y-4">
          <p className="text-red-400">{error ?? 'Dispute not found'}</p>
          <button onClick={() => navigate(-1)} className="btn btn-secondary">Go back</button>
        </div>
      </Layout>
    );
  }

  const trade = dispute.trade;
  const buyerId = dispute.opener_role === 'buyer' ? dispute.opened_by_id : dispute.against_user_id;
  const sellerId = dispute.opener_role === 'seller' ? dispute.opened_by_id : dispute.against_user_id;
  const buyerLabel = formatUserLabel(buyerId, userNames);
  const sellerLabel = formatUserLabel(sellerId, userNames);
  const openerLabel = formatUserLabel(dispute.opened_by_id, userNames);
  const againstLabel = formatUserLabel(dispute.against_user_id, userNames);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Link to={dispute.trade?.mode === 'otc' ? '/otc/disputes' : '/p2p/disputes'} className="text-slate-400 hover:text-white text-sm">
                ← Back to {dispute.trade?.mode === 'otc' ? 'OTC' : 'P2P'} disputes
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">Dispute #{dispute.id}</h1>
              <span className={`badge ${statusClass(dispute.status)}`}>{dispute.status.replace('_', ' ')}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityClass(dispute.priority)}`}>
                {dispute.priority}
              </span>
            </div>
            <p className="text-slate-400 mt-1 text-sm">
              Trade #{dispute.trade_id} · {dispute.trade_total ?? '—'} {dispute.fiat_currency}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {canResolve && dispute.status !== 'resolved' && dispute.status !== 'cancelled' && (
              <>
                {!dispute.assigned_admin_id ? (
                  <button onClick={handleAssignToMe} className="btn btn-secondary py-2 text-sm">
                    Assign to me
                  </button>
                ) : admin && Number(dispute.assigned_admin_id) === admin.id ? (
                  <button
                    onClick={handleUnassign}
                    disabled={isUnassigning}
                    className="btn btn-secondary py-2 text-sm disabled:opacity-50"
                  >
                    {isUnassigning ? 'Unassigning...' : 'Unassign'}
                  </button>
                ) : null}
                <button
                  onClick={() => { setRequestEvModalOpen(true); setRequestEvError(''); }}
                  className="btn btn-secondary py-2 text-sm"
                >
                  Request evidence
                </button>
                <button
                  onClick={() => { setResolveModalOpen(true); setResolveError(''); }}
                  className="btn btn-primary py-2 text-sm"
                >
                  Resolve
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-4 lg:col-span-2">
            <div className="card p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-300">Appeal details</h3>
              <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                <dt className="text-slate-500">Opened by</dt>
                <dd className="capitalize">
                  <button
                    type="button"
                    onClick={() =>
                      openParty(
                        dispute.opened_by_id,
                        `${dispute.opener_role} ${openerLabel}`,
                      )
                    }
                    className="text-indigo-400 hover:text-indigo-300 hover:underline font-medium"
                    title={`#${dispute.opened_by_id}`}
                  >
                    {dispute.opener_role} · {openerLabel}
                  </button>
                </dd>

                <dt className="text-slate-500">Against</dt>
                <dd>
                  <button
                    type="button"
                    onClick={() =>
                      openParty(dispute.against_user_id, againstLabel)
                    }
                    className="text-indigo-400 hover:text-indigo-300 hover:underline font-medium"
                    title={`#${dispute.against_user_id}`}
                  >
                    {againstLabel}
                  </button>
                </dd>

                <dt className="text-slate-500">Reason code</dt>
                <dd className="text-slate-200">{REASON_LABELS[dispute.reason_code] ?? dispute.reason_code}</dd>

                <dt className="text-slate-500">Opened at</dt>
                <dd className="text-slate-200">{new Date(dispute.created_at).toLocaleString()}</dd>

                {dispute.assigned_admin_id && (
                  <>
                    <dt className="text-slate-500">Assigned admin</dt>
                    <dd className="text-slate-200" title={`#${dispute.assigned_admin_id}`}>
                      {adminLabel(dispute.assigned_admin_id)}
                    </dd>
                  </>
                )}

                {dispute.resolved_at && (
                  <>
                    <dt className="text-slate-500">Resolved at</dt>
                    <dd className="text-slate-200">{new Date(dispute.resolved_at).toLocaleString()}</dd>
                    <dt className="text-slate-500">Outcome</dt>
                    <dd className="text-slate-200">{dispute.resolution}</dd>
                    {dispute.resolution_reason && (
                      <>
                        <dt className="text-slate-500">Admin note</dt>
                        <dd className="text-slate-200">{dispute.resolution_reason}</dd>
                      </>
                    )}
                  </>
                )}
              </dl>

              {dispute.reason_text && (
                <div className="pt-2">
                  <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Opener&apos;s description</p>
                  <p className="text-sm text-slate-200 whitespace-pre-wrap bg-slate-800/40 p-3 rounded">
                    {dispute.reason_text}
                  </p>
                </div>
              )}

              {dispute.counter_reason_text && (
                <div className="pt-2">
                  <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Counterparty&apos;s response</p>
                  <p className="text-sm text-slate-200 whitespace-pre-wrap bg-slate-800/40 p-3 rounded">
                    {dispute.counter_reason_text}
                  </p>
                </div>
              )}
              {!dispute.counter_reason_text && (
                <p className="text-xs text-slate-500 italic">Counterparty has not responded yet.</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <EvidenceList
                title={`Opener evidence (${dispute.opener_role})`}
                items={dispute.evidence ?? null}
                accent="text-emerald-400"
                disputeId={disputeId}
              />
              <EvidenceList
                title={`Counterparty evidence (${dispute.opener_role === 'buyer' ? 'seller' : 'buyer'})`}
                items={dispute.counter_evidence ?? null}
                accent="text-amber-400"
                disputeId={disputeId}
              />
            </div>

            <div className="card p-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">Mediation chat</h3>
              <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto pr-1">
                {messages.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">No messages yet.</p>
                ) : (
                  messages.map((m) => <MessageBubble key={m.id} message={m} disputeId={disputeId} />)
                )}
              </div>
              {canChat && dispute.status !== 'cancelled' && (
                <form onSubmit={handleSendMessage} className="mt-4 border-t border-slate-800 pt-3 space-y-2">
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    className="input min-h-[72px]"
                    rows={2}
                    placeholder="Type a message to both parties, or toggle internal note"
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-xs text-slate-400">
                      <input
                        type="checkbox"
                        checked={isInternalNote}
                        onChange={(e) => setIsInternalNote(e.target.checked)}
                      />
                      Internal admin note (not visible to users)
                    </label>
                    <button
                      type="submit"
                      disabled={isSendingMessage || !messageText.trim()}
                      className="btn btn-primary py-1.5 text-sm disabled:opacity-50"
                    >
                      {isSendingMessage ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {trade && (
              <div className="card p-4 space-y-2">
                <h3 className="text-sm font-semibold text-slate-300">Trade snapshot</h3>
                <dl className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Trade</dt>
                    <dd className="text-slate-200">#{trade.id}</dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-slate-500">Buyer</dt>
                    <dd>
                      <button
                        type="button"
                        onClick={() => buyerId && openParty(buyerId, `Buyer ${buyerLabel}`)}
                        className="text-emerald-400 hover:text-emerald-300 hover:underline font-medium"
                        title={`#${buyerId}`}
                      >
                        {buyerLabel}
                      </button>
                    </dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-slate-500">Seller</dt>
                    <dd>
                      <button
                        type="button"
                        onClick={() => sellerId && openParty(sellerId, `Seller ${sellerLabel}`)}
                        className="text-amber-400 hover:text-amber-300 hover:underline font-medium"
                        title={`#${sellerId}`}
                      >
                        {sellerLabel}
                      </button>
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Amount</dt>
                    <dd className="text-slate-200">{trade.amount} {trade.currency}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Total</dt>
                    <dd className="text-slate-200">{trade.total} {trade.fiat_currency}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Price</dt>
                    <dd className="text-slate-200">{trade.price}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Trade status</dt>
                    <dd className="text-slate-200">{trade.status}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Paid at</dt>
                    <dd className="text-slate-200 text-xs">{trade.paid_at ? new Date(trade.paid_at).toLocaleString() : '—'}</dd>
                  </div>
                  {trade.mode === 'otc' && (
                    <>
                      <div className="flex justify-between">
                        <dt className="text-slate-500">Section</dt>
                        <dd className="text-amber-300">OTC (cash in person)</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500">Meeting</dt>
                        <dd className="text-slate-200 text-right">
                          {trade.offer?.meeting_city ?? '—'}
                          {trade.offer?.meeting_details ? <span className="block text-xs text-slate-400">{trade.offer.meeting_details}</span> : null}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500">Release code</dt>
                        <dd className={trade.release_code_verified_at ? 'text-emerald-400 text-xs' : 'text-red-400 text-xs'}>
                          {trade.release_code_verified_at
                            ? `entered ${new Date(trade.release_code_verified_at).toLocaleString()}`
                            : `not entered · ${trade.release_code_attempts ?? 0} failed attempts`}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>
              </div>
            )}

            <TradeChatPanel disputeId={disputeId} buyerId={buyerId} />

            <AssignmentHistory events={dispute.events} adminLabel={adminLabel} />

            <Timeline events={dispute.events} />
          </div>
        </div>
      </div>

      <Modal
        isOpen={resolveModalOpen}
        onClose={() => setResolveModalOpen(false)}
        title="Resolve dispute"
      >
        <form onSubmit={handleResolve} className="space-y-4">
          <p className="text-sm text-slate-400">
            This releases escrow according to your chosen outcome and marks the dispute resolved.
          </p>
          {resolveError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {resolveError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Outcome</label>
            <select
              value={resolveOutcome}
              onChange={(e) => setResolveOutcome(e.target.value as DisputeResolution)}
              className="input"
            >
              <option value="release_to_buyer">Release to buyer (complete trade)</option>
              <option value="release_to_seller">Release to seller (complete trade)</option>
              <option value="cancel">Cancel (return escrow to seller)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Reason / note</label>
            <textarea
              value={resolveReason}
              onChange={(e) => setResolveReason(e.target.value)}
              className="input min-h-[80px]"
              rows={3}
              placeholder="Admin decision notes (stored on the dispute record)"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setResolveModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isResolving}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {isResolving ? 'Resolving...' : 'Resolve'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={requestEvModalOpen}
        onClose={() => setRequestEvModalOpen(false)}
        title="Request additional evidence"
      >
        <form onSubmit={handleRequestEvidence} className="space-y-4">
          {requestEvError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {requestEvError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Request from</label>
            <select
              value={requestEvFrom}
              onChange={(e) => setRequestEvFrom(e.target.value as 'buyer' | 'seller')}
              className="input"
            >
              <option value="buyer">Buyer</option>
              <option value="seller">Seller</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">What do you need?</label>
            <textarea
              value={requestEvNote}
              onChange={(e) => setRequestEvNote(e.target.value)}
              className="input min-h-[80px]"
              rows={3}
              placeholder="e.g. Please upload a bank statement covering the last 24 hours"
              required
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setRequestEvModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isRequestingEv || !requestEvNote.trim()}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {isRequestingEv ? 'Sending...' : 'Send request'}
            </button>
          </div>
        </form>
      </Modal>

      <UserDetailModal
        isOpen={partyUserId !== null}
        onClose={() => setPartyUserId(null)}
        userId={partyUserId}
        title={partyTitle}
      />
    </Layout>
  );
};

export default DisputeDetailPage;
